import {
  fetchShoppingList,
  normalizeShoppingListFolderIds,
} from 'src/services/favorite/useGetShoppingListMutatation';
import {
  GetShoppingListRequest,
  ShoppingListResponseItem,
} from 'src/types/faviriteProducts/getShoppingList';
import { ShoppingListExportSection } from 'src/utils/exportShoppingListsToExcel';

export interface ShoppingListExportFolder {
  folderId: number;
  listName: string;
}

const buildExportShoppingListRequest = (folderIds: number[]): GetShoppingListRequest => ({
  folderIds,
  queryOptions: {
    clientSide: true,
    skip: 0,
    take: 0,
    skipCalculatingTotalRecordsCount: false,
    sort: [],
    filters: [],
  },
});

const groupItemsByFolderId = (
  items: ShoppingListResponseItem[],
  folderIds: number[]
): Map<number, ShoppingListResponseItem[]> => {
  const itemsByFolderId = new Map<number, ShoppingListResponseItem[]>(
    folderIds.map((folderId) => [folderId, []])
  );

  const hasFolderIdOnItems = items.some((item) => item.folderId != null);

  if (!hasFolderIdOnItems) {
    if (folderIds.length === 1) {
      itemsByFolderId.set(folderIds[0], items);
    }
    return itemsByFolderId;
  }

  items.forEach((item) => {
    const { folderId } = item;
    if (folderId == null || !itemsByFolderId.has(folderId)) {
      return;
    }
    itemsByFolderId.get(folderId)!.push(item);
  });

  return itemsByFolderId;
};

export async function fetchShoppingListsForExport(
  folders: ShoppingListExportFolder[]
): Promise<ShoppingListExportSection[]> {
  const uniqueFolders = Array.from(
    new Map(
      folders
        .filter((folder) => Number.isFinite(folder.folderId) && folder.folderId > 0)
        .map((folder) => [folder.folderId, folder])
    ).values()
  );

  if (uniqueFolders.length === 0) {
    return [];
  }

  const folderIds = normalizeShoppingListFolderIds(
    uniqueFolders.map((folder) => folder.folderId)
  );
  const response = await fetchShoppingList(buildExportShoppingListRequest(folderIds));
  const itemsByFolderId = groupItemsByFolderId(response.items ?? [], folderIds);

  return uniqueFolders.map((folder) => ({
    listName: folder.listName,
    items: itemsByFolderId.get(folder.folderId) ?? [],
  }));
}
